import React, { createContext, useContext, useState, useEffect } from 'react';
import { formatNumber } from '../utils/calculations';

const VatContext = createContext();

export const useVat = () => {
  const context = useContext(VatContext);
  if (!context) {
    throw new Error('useVat must be used within a VatProvider');
  }
  return context;
};

export const VatProvider = ({ children }) => {
  const [vatPercentage, setVatPercentage] = useState(15.00);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Only fetch VAT settings if user is authenticated
    const token = localStorage.getItem('token');
    if (token) {
      fetchVatSettings();
    } else {
      setIsLoading(false);
    }
  }, []);

  const fetchVatSettings = async () => {
    try {
      const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';
      const response = await fetch(`${API_BASE_URL}/vat-settings`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setVatPercentage(data.vat_percentage);
      }
    } catch (error) {
      console.error('Error fetching VAT settings:', error);
      // Keep default 15% if API call fails
    } finally {
      setIsLoading(false);
    }
  };

  const updateVatPercentage = (newPercentage) => {
    console.log('VAT context updating from', vatPercentage, 'to', newPercentage);
    setVatPercentage(newPercentage);
  };

  const calculatePriceWithVat = (priceWithoutVat) => {
    if (!priceWithoutVat || isNaN(parseFloat(priceWithoutVat))) return '';
    const price = parseFloat(priceWithoutVat);
    return formatNumber(price * (1 + (vatPercentage / 100)), 2);
  };

  const calculatePriceWithoutVat = (priceWithVat) => {
    if (!priceWithVat || isNaN(parseFloat(priceWithVat))) return '';
    const price = parseFloat(priceWithVat);
    return formatNumber(price / (1 + (vatPercentage / 100)), 2);
  };

  const value = {
    vatPercentage,
    isLoading,
    updateVatPercentage,
    calculatePriceWithVat,
    calculatePriceWithoutVat,
    fetchVatSettings
  };

  return (
    <VatContext.Provider value={value}>
      {children}
    </VatContext.Provider>
  );
};
